<template>
  <div class="cmp-overlay" @click.self="cerrar">
    <div class="cmp-caja" role="dialog" aria-modal="true" aria-label="Comparar con archivo">
      <!-- Encabezado -->
      <div class="cmp-header">
        <div>
          <div class="small text-muted">Comparar con archivo</div>
          <h5 class="mb-0">{{ prestador || `Prestador ${idPrestador}` }}</h5>
        </div>
        <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrar"></button>
      </div>

      <!-- Contenido -->
      <div class="cmp-body">
        <!-- Paso 1: elegir el archivo -->
        <div v-if="!resultado">
          <p class="text-muted">
            Suba un archivo CSV con las columnas <code>codigo</code> y <code>valor</code>.
            Se mostrarán las tarifas del prestador cuyo valor en la tabla sea
            <strong>menor</strong> que el valor del archivo.
          </p>
          <input
            type="file"
            class="form-control"
            accept=".csv,.txt"
            :disabled="cargando"
            @change="onArchivo"
          />
          <div class="form-text">
            Separador <code>;</code>, <code>,</code> o tabulador. Decimales con coma o punto. Máximo 10 MB.
          </div>
          <div v-if="error" class="alert alert-danger mt-3 mb-0" role="alert">{{ error }}</div>
        </div>

        <!-- Paso 2: resultado -->
        <template v-else>
          <div class="text-muted small mb-2">
            Archivo: <strong>{{ archivo ? archivo.name : "" }}</strong>
          </div>

          <!-- Resumen -->
          <div class="row g-2 mb-3">
            <div class="col-6 col-md">
              <div class="resumen"><div class="num">{{ resultado.totalFilasArchivo }}</div>filas del archivo</div>
            </div>
            <div class="col-6 col-md">
              <div class="resumen"><div class="num">{{ resultado.coincidencias }}</div>coincidencias</div>
            </div>
            <div class="col-6 col-md">
              <div class="resumen resumen-alerta">
                <div class="num">{{ resultado.totalMenores }}</div>valor menor al archivo
              </div>
            </div>
            <div class="col-6 col-md">
              <div class="resumen"><div class="num">{{ resultado.codigosNoEncontrados.length }}</div>no encontrados</div>
            </div>
            <div class="col-6 col-md">
              <div class="resumen"><div class="num">{{ resultado.filasInvalidas.length }}</div>filas inválidas</div>
            </div>
          </div>

          <!-- Avisos sobre el archivo -->
          <details v-if="resultado.filasInvalidas.length" class="mb-2">
            <summary class="text-danger">
              {{ resultado.filasInvalidas.length }} fila(s) inválida(s) que no se compararon
            </summary>
            <table class="table table-sm table-bordered mt-2 mb-0">
              <thead class="table-light"><tr><th style="width:90px">Línea</th><th>Motivo</th></tr></thead>
              <tbody>
                <tr v-for="f in resultado.filasInvalidas.slice(0, 100)" :key="f.linea">
                  <td>{{ f.linea }}</td><td>{{ f.motivo }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="resultado.filasInvalidas.length > 100" class="small text-muted">
              Se muestran las primeras 100.
            </div>
          </details>

          <details v-if="resultado.codigosDuplicadosArchivo.length" class="mb-2">
            <summary class="text-warning-emphasis">
              {{ resultado.codigosDuplicadosArchivo.length }} código(s) repetido(s) en el archivo (se usó el último valor)
            </summary>
            <div class="mt-2">
              <span v-for="c in resultado.codigosDuplicadosArchivo.slice(0, 200)" :key="c" class="badge bg-warning text-dark me-1 mb-1">{{ c }}</span>
            </div>
          </details>

          <details v-if="resultado.codigosNoEncontrados.length" class="mb-3">
            <summary>
              {{ resultado.codigosNoEncontrados.length }} código(s) del archivo que este prestador no tiene
            </summary>
            <div class="mt-2">
              <span v-for="c in resultado.codigosNoEncontrados.slice(0, 200)" :key="c" class="badge bg-secondary me-1 mb-1">{{ c }}</span>
              <div v-if="resultado.codigosNoEncontrados.length > 200" class="small text-muted">
                Se muestran los primeros 200.
              </div>
            </div>
          </details>

          <!-- Tabla de resultados -->
          <div v-if="resultado.totalMenores === 0" class="alert alert-success mb-0" role="alert">
            Ningún valor de la tabla es menor que el del archivo.
          </div>

          <template v-else>
            <div class="table-responsive">
              <table class="table table-hover table-bordered table-sm mb-2">
                <thead class="table-dark">
                  <tr>
                    <th class="text-center">#</th>
                    <th>Código</th>
                    <th>Elemento</th>
                    <th>Tarifario</th>
                    <th class="text-end">Valor tabla</th>
                    <th class="text-end">Valor archivo</th>
                    <th class="text-end">Diferencia</th>
                    <th class="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(f, idx) in filasPagina" :key="f.idTarifaPres">
                    <td class="text-center">{{ (pagina - 1) * porPagina + idx + 1 }}</td>
                    <td>{{ f.elementoCodigo }}</td>
                    <td class="col-nombre" :title="f.elementoNombre">{{ f.elementoNombre }}</td>
                    <td>{{ f.tarifaConvNombre ?? "—" }}</td>
                    <td class="text-end fw-bold">{{ moneda(f.valorTabla) }}</td>
                    <td class="text-end fw-bold">{{ moneda(f.valorArchivo) }}</td>
                    <td class="text-end text-danger fw-semibold">{{ moneda(f.diferencia) }}</td>
                    <td class="text-end text-danger">{{ pct(f.porcentaje) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <span class="small text-muted">
                Página {{ pagina }} de {{ totalPaginas }} — {{ resultado.totalMenores }} registros
              </span>
              <div class="d-flex align-items-center gap-2">
                <select v-model.number="porPagina" class="form-select form-select-sm" style="width:75px">
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" :disabled="pagina === 1" @click="pagina--">‹</button>
                  <button class="btn btn-outline-secondary" :disabled="pagina === totalPaginas" @click="pagina++">›</button>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- Pie -->
      <div class="cmp-footer">
        <template v-if="!resultado">
          <button type="button" class="btn btn-secondary" @click="cerrar">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="!archivo || cargando" @click="comparar">
            <span v-if="cargando" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ cargando ? "Comparando..." : "Comparar" }}
          </button>
        </template>
        <template v-else>
          <button type="button" class="btn btn-outline-secondary me-auto" @click="reiniciar">
            Comparar otro archivo
          </button>
          <button
            type="button"
            class="btn btn-outline-success"
            :disabled="resultado.totalMenores === 0"
            title="Exportar CSV"
            @click="exportarCSV"
          >CSV</button>
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="resultado.totalMenores === 0"
            title="Exportar Excel"
            @click="exportarExcel"
          >Excel</button>
          <button type="button" class="btn btn-secondary" @click="cerrar">Cerrar</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import * as XLSX from "xlsx";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB (debe coincidir con spring.servlet.multipart.max-file-size)

export default {
  name: "CompararArchivoModal",
  props: {
    idPrestador: { type: [Number, String], required: true },
    prestador:   { type: String, default: "" },   // razón social, solo para el título
  },
  emits: ["cerrar"],
  data() {
    return {
      archivo:   null,
      cargando:  false,
      error:     "",
      resultado: null,
      pagina:    1,
      porPagina: 25,
    };
  },
  computed: {
    totalPaginas() {
      return Math.max(1, Math.ceil((this.resultado?.resultados.length ?? 0) / this.porPagina));
    },
    filasPagina() {
      const ini = (this.pagina - 1) * this.porPagina;
      return (this.resultado?.resultados ?? []).slice(ini, ini + this.porPagina);
    },
  },
  watch: {
    porPagina() { this.pagina = 1; },
  },
  mounted() {
    window.addEventListener("keydown", this.onTecla);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onTecla);
  },
  methods: {
    cerrar() {
      this.$emit("cerrar");
    },
    onTecla(e) {
      if (e.key === "Escape" && !this.cargando) this.cerrar();
    },
    onArchivo(e) {
      this.error = "";
      const f = e.target.files && e.target.files[0];
      this.archivo = null;
      if (!f) return;
      if (!/\.(csv|txt)$/i.test(f.name)) {
        this.error = "El archivo debe ser .csv o .txt.";
        return;
      }
      if (f.size > MAX_BYTES) {
        this.error = "El archivo supera los 10 MB.";
        return;
      }
      this.archivo = f;
    },
    async comparar() {
      if (!this.archivo) return;
      this.cargando = true;
      this.error = "";
      try {
        const datos = new FormData();
        datos.append("archivo", this.archivo);
        // Se indica multipart por si la instancia `api` fija application/json por defecto;
        // axios reemplaza el valor y agrega el boundary automáticamente.
        const res = await api.post(
          `/tarifa-pres/prestador/${this.idPrestador}/comparar-csv`,
          datos,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        this.resultado = res.data;
        this.pagina = 1;
      } catch (e) {
        console.error("Error comparando el archivo:", e);
        this.error = e.response?.data?.mensaje || "No se pudo comparar el archivo.";
      } finally {
        this.cargando = false;
      }
    },
    reiniciar() {
      this.resultado = null;
      this.archivo = null;
      this.error = "";
      this.pagina = 1;
    },
    // ── Formato ────────────────────────────────────────────
    moneda(v) {
      if (v === null || v === undefined) return "—";
      return Number(v).toLocaleString("es-EC", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    },
    pct(v) {
      if (v === null || v === undefined) return "—";
      return `${Number(v).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    // ── Exportación (usa el resultado completo ya descargado) ──
    filasExport() {
      return this.resultado.resultados.map((f, i) => ({
        "#": i + 1,
        "Código": f.elementoCodigo,
        "Elemento": f.elementoNombre,
        "Tarifario": f.tarifaConvNombre ?? "",
        "Valor tabla": f.valorTabla,
        "Valor archivo": f.valorArchivo,
        "Diferencia": f.diferencia,
        "%": f.porcentaje,
      }));
    },
    exportarCSV() {
      const filas = this.filasExport();
      const cols = Object.keys(filas[0]);
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") || str.includes('"') ? `"${str}"` : str;
      };
      const lineas = [cols.join(";"), ...filas.map((f) => cols.map((c) => escape(f[c])).join(";"))];
      const blob = new Blob(["\uFEFF" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "comparacion_tarifas_vs_archivo.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    exportarExcel() {
      const hoja = XLSX.utils.json_to_sheet(this.filasExport());
      hoja["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 60 }, { wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Comparación");
      XLSX.writeFile(libro, "comparacion_tarifas_vs_archivo.xlsx");
    },
  },
};
</script>

<style scoped>
.cmp-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cmp-caja {
  background: #fff;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 1100px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
}
.cmp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.cmp-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
}
.cmp-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
}
.resumen {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
}
.resumen .num {
  font-size: 1.4rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.2;
}
.resumen-alerta {
  background: #f8d7da;
  border-color: #f1aeb5;
}
.resumen-alerta .num {
  color: #842029;
}
.col-nombre {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
details summary {
  cursor: pointer;
}
</style>